import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link, useHistory, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import GlobalModal from '../../components/GlobalModal';
import CommentModal from './CommentModal';
import AttachmentModal from './AttachmentModal';
import RecipientsModal from './RecipientsModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDate from '../../components/DisplayDate';

// ICONS
import { ReactComponent as CommentIcon } from '../../assets/svg/comments.svg';
import { ReactComponent as AttachmentIcon } from '../../assets/svg/attachment.svg';
import { ReactComponent as TickIcon } from '../../assets/svg/tick-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/svg/plus-icon.svg';
import { ReactComponent as QuestionMarkIcon } from '../../assets/svg/questionmark.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import {
	DomainChecklistActionStatus,
	SafetyChecklistChecklistChecklistItem,
	SafetyChecklistChecklistSafetyChecklist,
	DomainChecklistStatusType,
	SafetyChecklistSubmitCommand
} from '../../api/models';

type ChecklistItemFormValues = Pick<
	SafetyChecklistChecklistChecklistItem,
	'id' | 'text' | 'hasComment' | 'attachments'
> & {
	status: DomainChecklistActionStatus | '';
};

type SafetyChecklistDetailFormValues = {
	additionalCommentsInput: string;
	overdueReasonInput: string;
	checklistItems: ChecklistItemFormValues[];
};

const mapChecklistItemsToFormValues = (
	items: SafetyChecklistChecklistChecklistItem[] | undefined
): ChecklistItemFormValues[] =>
	(items ?? []).map((item) => ({
		id: item.id ?? '',
		text: item.text,
		status: item.status ?? '',
		hasComment: item.hasComment,
		attachments: item.attachments
	}));

const SafetyChecklistsDetail: React.FC = () => {
	const history = useHistory();

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get Id from params
	const params = useParams<RouteParams>();
	const checklistId = params.id;

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state and Initial Data
	const initialData = {
		id: undefined,
		identifier: undefined,
		purpose: undefined,
		template: undefined,
		status: undefined,
		creator: undefined,
		statusDescription: undefined,
		additionalComments: undefined,
		overdueReason: undefined,
		checklistItems: []
	};

	const [safetyChecklistsDetailData, setSafetyChecklistsDetailData] =
		React.useState<SafetyChecklistChecklistSafetyChecklist>(initialData);

	const getSafetyChecklistsDetailData = async (useLoading: boolean = true) => {
		if (useLoading) {
			setIsLoading(true);
		}

		const response = await apibridge.getSafetyChecklistChecklist(checklistId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialData } = response;

			setIsLoading(false);

			setSafetyChecklistsDetailData(result);
		}
	};

	React.useEffect(() => {
		getSafetyChecklistsDetailData();
	}, []);

	const {
		id,
		identifier,
		creator,
		status,
		statusDescription,
		template,
		canComplete,
		canEdit,
		checklistItems,
		purpose,
		additionalComments,
		overdueReason,
		assignmentType,
		conditionStartDate,
		conditionEndDate,
		frequency,
		frequencyDescription,
		assignmentTypeDescription,
		recipientCount,
		recipients,
		isOverdue
	} = safetyChecklistsDetailData!;

	// Submit form data
	const onFormSubmit = async (values: SafetyChecklistDetailFormValues) => {
		setFormErrorMessage('');
		setIsLoading(true);

		const submitData: SafetyChecklistSubmitCommand = {
			id: params.id,
			...(isOverdue === true
				? { overdueReason: String(values.overdueReasonInput ?? '') }
				: { additionalComments: String(values.additionalCommentsInput ?? '') })
		};

		const response = await apibridge.postSafetyChecklistSubmit(submitData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/safety-checklists');

			useUpdateTotalNotification();
		}
	};

	const initialDetailFormValues = React.useMemo<SafetyChecklistDetailFormValues>(
		() => ({
			additionalCommentsInput: additionalComments ?? '',
			overdueReasonInput: overdueReason ?? '',
			checklistItems: mapChecklistItemsToFormValues(checklistItems)
		}),
		[additionalComments, overdueReason, checklistItems]
	);

	const validationSchema = React.useMemo(
		() =>
			status !== DomainChecklistStatusType.ToDo
				? Yup.object({
						additionalCommentsInput: Yup.string(),
						overdueReasonInput: Yup.string(),
						checklistItems: Yup.array()
					})
				: Yup.object({
						...(isOverdue === true
							? { overdueReasonInput: Yup.string().required('Required') }
							: { additionalCommentsInput: Yup.string().required('Required') }),
						checklistItems: Yup.array()
							.of(
								Yup.object({
									id: Yup.string(),
									text: Yup.string().optional(),
									status: Yup.mixed().optional(),
									hasComment: Yup.boolean().optional(),
									attachments: Yup.number().optional()
								})
							)
							.test(
								'all-checked',
								'Please select Yes, No, or N/A for every checklist item.',
								(items) =>
									!items?.length ||
									items.every(
										(item) =>
											item?.status === DomainChecklistActionStatus.Yes ||
											item?.status === DomainChecklistActionStatus.No ||
											item?.status === DomainChecklistActionStatus.NotApplicable
									)
							)
					}),
		[isOverdue, status]
	);

	// Update Checklist status
	const updateStatus = async (id: string, status: DomainChecklistActionStatus) => {
		const checklistStatusData = {
			id: id,
			status: status
		};

		const response = await apibridge.postSafetyChecklistAction(checklistStatusData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);
		}
	};

	// State to manage Modal and pass in id
	const [isMounted, setIsMounted] = React.useState<boolean>(false);

	const [showCommentModal, setShowCommentModal] = React.useState<boolean>(false);

	const [commentId, setCommentId] = React.useState<string>('');

	const commentModalControl = (itemId: string | undefined) => {
		if (itemId !== undefined) {
			setCommentId(itemId);
			setShowCommentModal(true);
		}
	};

	React.useEffect(() => {
		if (showCommentModal === false && isMounted === true) {
			getSafetyChecklistsDetailData(false);
		}

		setIsMounted(true);
	}, [showCommentModal]);

	const [showAttachmentModal, setShowAttachmentModal] = React.useState<boolean>(false);

	const [attachmentId, setAttachmentId] = React.useState<string>('');

	const [showRecipientsModal, setShowRecipientsModal] = React.useState<boolean>(false);

	const attachmentModalControl = (itemId: string | undefined) => {
		if (itemId !== undefined) {
			setAttachmentId(itemId);
			setShowAttachmentModal(true);
		}
	};

	React.useEffect(() => {
		if (showAttachmentModal === false && isMounted === true) {
			getSafetyChecklistsDetailData(false);
		}

		setIsMounted(true);
	}, [showAttachmentModal]);

	return (
		<div className="page-general-layout">
			<Formik
				initialValues={initialDetailFormValues}
				validationSchema={validationSchema}
				onSubmit={onFormSubmit}
				enableReinitialize
			>
				{(formik) => {
					return (
						<Form translate="yes" className="general-form mt-0">
							{!!formErrorMessage ? (
								<div className="form-validation-error">{formErrorMessage}</div>
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="general-form-layout-wrapper">
													<div className="form-main w-100">
														<h1 className="text-capitalized mbtom-20">{purpose}</h1>

														<p className="sub-grey-text mbtom-0">Mark items off your checklist.</p>

														<div className="complaints-detail-general-info no-border">
															<div className="general-info-left">
																<div className="info-col">
																	<div className="info-title">Created by</div>

																	<div className="info-detail">{creator}</div>
																</div>

																<div className="info-col">
																	<div className="info-title">ID</div>

																	<div className="info-detail">{identifier}</div>
																</div>

																<div className="info-col">
																	<div className="info-title">Template</div>

																	<div className="info-detail">{template ? template : 'None'}</div>
																</div>

																<div className="info-col">
																	<div className="info-title">Status</div>

																	<div className="info-detail">{isOverdue ? 'Overdue' : statusDescription}</div>
																</div>
															</div>
														</div>

														<h3>Details</h3>

														<div className="general-divider"></div>

														{frequencyDescription && <p>{frequencyDescription}</p>}

														{conditionStartDate && (
															<p>
																Start Date: <DisplayDate>{conditionStartDate}</DisplayDate>
															</p>
														)}

														{conditionEndDate && (
															<p>
																End Date: <DisplayDate>{conditionEndDate}</DisplayDate>
															</p>
														)}

														{recipientCount && (
															<div className="d-flex align-items-center gap-3 mbtom-16">
																<p className="mb-0">
																	{recipientCount} {recipientCount === 1 ? 'recipient' : 'recipients'}
																</p>

																<Button className="btn-text" onClick={() => setShowRecipientsModal(true)}>
																	View
																</Button>
															</div>
														)}

														{assignmentType === 'Individual' && (
															<>
																<label className="mbtom-8 fw-medium text-grey-5">Individual recipients</label>

																<p className="mb-0 fw-medium text-grey-4">
																	Each recipient is required to complete the checklist.
																</p>
															</>
														)}

														{assignmentType === 'Group' && (
															<>
																<label className="mbtom-8 fw-medium text-grey-5">Group recipients</label>

																<p className="mb-0 fw-medium text-grey-4">
																	Assigned to multiple users, only one recipient from the group is required to complete
																	the checklist.
																</p>
															</>
														)}

														<h3 className="mtop-80 mbtom-16">Checklist items to be completed</h3>

														<ul>
															{formik.values.checklistItems.length > 0 &&
																formik.values.checklistItems.map((item, index) => {
																	const fieldName = `checklistItems.${index}.status`;

																	const setItemStatus = (value: DomainChecklistActionStatus) => {
																		if (!item.id) {
																			return;
																		}
																		formik.setFieldValue(fieldName, value);
																		updateStatus(item.id, value);
																	};

																	return (
																		<li className="list-item-with-details" key={item.id || index}>
																			<div className="detail-left">{item.text}</div>
																			<div className="detail-right">
																				<div className="detail-group">
																					<input
																						id={`${item.id}-Yes`}
																						disabled={!canComplete || !isOrganisationActive}
																						type="radio"
																						name={fieldName}
																						value={DomainChecklistActionStatus.Yes}
																						checked={item.status === DomainChecklistActionStatus.Yes}
																						onChange={() => setItemStatus(DomainChecklistActionStatus.Yes)}
																					/>
																					<label htmlFor={`${item.id}-Yes`}>Yes</label>
																				</div>

																				<div className="detail-group">
																					<input
																						id={`${item.id}-No`}
																						disabled={!canComplete || !isOrganisationActive}
																						type="radio"
																						name={fieldName}
																						value={DomainChecklistActionStatus.No}
																						checked={item.status === DomainChecklistActionStatus.No}
																						onChange={() => setItemStatus(DomainChecklistActionStatus.No)}
																					/>
																					<label htmlFor={`${item.id}-No`}>No</label>
																				</div>

																				<div className="detail-group">
																					<input
																						id={`${item.id}-NotApplicable`}
																						disabled={!canComplete || !isOrganisationActive}
																						type="radio"
																						name={fieldName}
																						value={DomainChecklistActionStatus.NotApplicable}
																						checked={item.status === DomainChecklistActionStatus.NotApplicable}
																						onChange={() => setItemStatus(DomainChecklistActionStatus.NotApplicable)}
																					/>
																					<label htmlFor={`${item.id}-NotApplicable`}>N/A</label>
																				</div>

																				<Button
																					className={`icon-group ${item.hasComment ? 'active' : ''}`}
																					onClick={() => commentModalControl(item.id)}
																					disabled={!isOrganisationActive}
																				>
																					<CommentIcon className="content-icon" />

																					{item.hasComment ? (
																						<div className="icon-wrapper show">
																							<TickIcon className="info-icon-small " />
																						</div>
																					) : (
																						<div className="icon-wrapper">
																							<PlusIcon className="info-icon" />
																						</div>
																					)}
																				</Button>

																				<Button
																					className={`icon-group ${item.attachments !== 0 ? 'active' : ''}`}
																					onClick={() => attachmentModalControl(item.id)}
																					disabled={!isOrganisationActive}
																				>
																					<AttachmentIcon className="content-icon" />

																					{item.attachments === 0 ? (
																						<div className="icon-wrapper">
																							<PlusIcon className="info-icon" />
																						</div>
																					) : (
																						<div className="icon-wrapper has-number">
																							<div className="info-icon">{item.attachments}</div>
																						</div>
																					)}
																				</Button>
																			</div>
																		</li>
																	);
																})}
														</ul>

														{status === DomainChecklistStatusType.ToDo &&
															formik.submitCount > 0 &&
															typeof formik.errors.checklistItems === 'string' && (
																<div className="error-message">{formik.errors.checklistItems}</div>
															)}

														<div className="form-group mtop-40">
															{status === DomainChecklistStatusType.ToDo && (
																<>
																	{isOverdue === true ? (
																		<>
																			<label htmlFor="overdueReasonInput" className="label">
																				Please provide comment on overdue checklist reason *
																			</label>

																			<div className="form-field">
																				<Field
																					disabled={
																						safetyChecklistsDetailData.status === DomainChecklistStatusType.Completed ||
																						!safetyChecklistsDetailData.canComplete ||
																						!isOrganisationActive
																					}
																					as="textarea"
																					name="overdueReasonInput"
																					id="overdueReasonInput"
																					type="text"
																					className="form-input"
																				/>

																				<ErrorMessage
																					name="overdueReasonInput"
																					component="div"
																					className="error-message"
																				/>
																			</div>
																		</>
																	) : (
																		<>
																			<label htmlFor="additionalCommentsInput" className="label">
																				Additional Comments
																			</label>

																			<div className="form-field">
																				<Field
																					disabled={
																						safetyChecklistsDetailData.status === DomainChecklistStatusType.Completed ||
																						!safetyChecklistsDetailData.canComplete ||
																						!isOrganisationActive
																					}
																					as="textarea"
																					name="additionalCommentsInput"
																					id="additionalCommentsInput"
																					type="text"
																					className="form-input"
																				/>

																				<ErrorMessage
																					name="additionalCommentsInput"
																					component="div"
																					className="error-message"
																				/>
																			</div>
																		</>
																	)}
																</>
															)}

															{status === DomainChecklistStatusType.Completed && (
																<>
																	{overdueReason ? (
																		<>
																			<label htmlFor="overdueReasonInput" className="label">
																				Please provide comment on overdue checklist reason *
																			</label>

																			<div className="form-field">
																				<Field
																					disabled
																					as="textarea"
																					name="overdueReasonInput"
																					id="overdueReasonInput"
																					type="text"
																					className="form-input"
																				/>
																			</div>
																		</>
																	) : (
																		<>
																			<label htmlFor="additionalCommentsInput" className="label">
																				Additional Comments
																			</label>

																			<div className="form-field">
																				<Field
																					disabled
																					as="textarea"
																					name="additionalCommentsInput"
																					id="additionalCommentsInput"
																					type="text"
																					className="form-input"
																				/>
																			</div>
																		</>
																	)}
																</>
															)}
														</div>
													</div>
												</div>

												{safetyChecklistsDetailData.status === DomainChecklistStatusType.Completed ? (
													<a href={safetyChecklistsDetailData.summaryPdfUrl} download className="btn-secondary">
														<DownloadIcon /> Download Checklist Summary
													</a>
												) : canComplete ? (
													<>
														<div className="general-divider"></div>

														{isOrganisationActive && (
															<div className="form-button-group">
																<Button
																	type="submit"
																	disabled={
																		safetyChecklistsDetailData.status === DomainChecklistStatusType.Completed ||
																		!isOrganisationActive
																	}
																	className="btn-primary submit-btn complaints-submit-page w-auto"
																>
																	Complete Checklist
																</Button>
															</div>
														)}
													</>
												) : null}

												{canComplete ? (
													<div className="general-form-guide wide">
														<div className="guide-left">
															<QuestionMarkIcon />
														</div>

														<div className="guide-right">
															<h2 className="guide-heading">Not ready to complete this checklist?</h2>

															<p className="guide-content">
																Please discuss any required changes to this checklist with the staff member who created
																it.
															</p>
														</div>
													</div>
												) : null}
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</Form>
					);
				}}
			</Formik>

			<GlobalModal isOpen={showCommentModal} setIsOpen={setShowCommentModal} titleText="Comment">
				<CommentModal id={commentId} canEdit={canComplete} />
			</GlobalModal>

			<GlobalModal isOpen={showAttachmentModal} setIsOpen={setShowAttachmentModal} titleText="Attachments">
				<AttachmentModal attachmentId={attachmentId} canEdit={canComplete} />
			</GlobalModal>

			<GlobalModal isOpen={showRecipientsModal} setIsOpen={setShowRecipientsModal} titleText="Checklist recipients">
				<RecipientsModal recipients={recipients} />
			</GlobalModal>
		</div>
	);
};

export default SafetyChecklistsDetail;
